import {
  useCallback,
  useEffect, useMemo,
  useState,
} from 'react';
import { useWaitForDataReady } from '~/map/afterMigrationMapPrepareModal/useWaitForDataReady';
import { useBoundaryIdsFromBoundaryFilter } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryIdsFromBoundaryFilter';
import { useZoomToBoundary } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useZoomToBoundary';
import { useBoundaryLocationsFilters } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.selectors';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import type { BoundingBox } from '../../boundingBox';

export const useIsolateBoundariesInitialZoom = (onZoom?: (boundingBox: BoundingBox) => void) => {
  const mapId = useMapIdSelector();
  const boundaryLocationsFilters = useBoundaryLocationsFilters();
  const { zoomToBoundary } = useZoomToBoundary();
  const boundariesMap = useBoundaryIdsFromBoundaryFilter(boundaryLocationsFilters.isolated);
  const isMapAndDataReady = useWaitForDataReady();

  const [requestedZoom, setRequestedZoom] = useState(false);

  const isIsolatedBoundaryActive = !!boundaryLocationsFilters.isolated.entries.length;

  const requestIsolateBoundaryInitialZoom = useCallback(() => {
    setRequestedZoom(true);
  }, []);

  const isolatedBoundaries = useMemo(() => (
    isMapAndDataReady
      ? boundariesMap.entries().reduce((acc, [boundaryGroupId, boundaryIds]) => (
        [...acc, ...Array.from(boundaryIds.values()).map(boundaryId => ({ boundaryGroupId, boundaryId }))]
      ), [])
      : undefined
  ), [boundariesMap, isMapAndDataReady]);

  useEffect(() => {
    if (requestedZoom && isIsolatedBoundaryActive && isMapAndDataReady && isolatedBoundaries) {
      zoomToBoundary(isolatedBoundaries, onZoom);
      setRequestedZoom(false);
    }
  }, [requestedZoom, boundariesMap, isIsolatedBoundaryActive, isMapAndDataReady, isolatedBoundaries, zoomToBoundary, onZoom]);

  useEffect(() => {
    setRequestedZoom(false);
  }, [mapId]);

  return {
    isIsolatedBoundaryActive,
    requestIsolateBoundaryInitialZoom,
  };
};
